/**
 * /admin/audit — audit-log reader (#429 Wave 3 Track G).
 *
 * Paginated investigation surface over the `auditEvents` collection
 * Wave 2 already populates. Filter bar at the top, table below, "Load
 * more" cursor at the bottom (offset-based — easy to reason about,
 * matches the underlying `auditRepo.query` shape, and `total` is
 * deliberately NOT returned by the server because `countDocuments`
 * dominates the request budget once the collection grows).
 *
 * **Filters:**
 *   - Actor email (server resolves email → actorUserId via the users
 *     collection; audit row stays id-only on disk per AU-3
 *     minimization). Email is the only actor-side filter the UI
 *     surfaces — the earlier "Actor id (prefix)" input was retired
 *     because email is the more natural starting point for an
 *     investigator; the server still accepts `actorUserId` for
 *     direct-URL queries.
 *   - Action key (free-text + Combobox suggestions from
 *     `lib/auditCatalog`). Free-text so an investigator can query
 *     an unknown / new / typo'd action without waiting on a frontend
 *     deploy. The suggestion list is the discoverability scaffold.
 *   - Target type (same shape — free-text + Combobox).
 *   - Time window — preset dropdown (last 24h / last 7d / last 30d /
 *     all time / custom). "Custom" exposes two date inputs.
 *
 * Default time window is `last 7d` — the common-case investigation
 * scope; an investigator who needs further back picks "all time" or
 * "custom" explicitly. The default keeps the initial page load
 * scoped so a 6-month-old collection doesn't dump the kitchen sink.
 *
 * URL-backed filters — every field round-trips through
 * `useSearchParams` (same pattern as `/admin/analytics`). Refresh,
 * back/forward, and link-sharing all preserve the filter (and the
 * sort direction).
 *
 * **Columns.** Timestamp / Actor / Action / Org / Workspace / App /
 * Variant / Target / Result / Meta. The four context columns
 * (org/workspace/app/variant) come from the server-resolved chain
 * in the response's `contexts` map; missing entries render as `—`
 * (the chain doesn't apply for system-level target types like
 * `model_pricing` / `route` / `session`, and `actorOrgId` is the
 * fallback when an event has no domain target).
 *
 * **Sort.** Timestamp is the only sortable column — the server's
 * `{ ts: -1 }` index serves it cheaply. Click the header to toggle
 * desc (default) ↔ asc. Other column headers stay static; client-
 * side sort would lie once "Load more" is in play, and server-side
 * multi-column sort would need new indexes that an investigation
 * tool doesn't justify.
 *
 * **Autorefresh.** The page polls `/api/admin/audit/count` every
 * ~10s with `since=topRowTs` and surfaces a clickable "N new
 * events" badge above the table when the count is non-zero. Clicking
 * refetches from offset 0. No silent prepending — would shift the
 * user's scroll position and the "Load more" offset under them, a
 * known UX trap for investigation surfaces.
 *
 * **Authorization.** `/admin/audit` is wrapped by `<RequireSuperadmin>`
 * upstream in App.tsx, and the backend route is gated by
 * `requireSession` + `requireSuperadmin`. The backend gate is the
 * actual boundary; this page is UX defense in depth.
 *
 * **Safety on `meta`.** Audit rows can carry operator-supplied values
 * inside `meta` (e.g. an organization name an admin typed when
 * inviting a member). The meta column ALWAYS renders as formatted
 * JSON inside a `<pre>` — never via `dangerouslySetInnerHTML`. React's
 * text-content escaping handles any embedded `<` / `&` / quotes.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Combobox,
  Input,
  Label,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui'
import {
  adminAuditAPI,
  type AuditContext,
  type AuditEventRow,
  type AuditFilter,
} from '@/lib/api'
import { AUDIT_ACTION_KEYS, AUDIT_TARGET_TYPE_KEYS } from '@/lib/auditCatalog'
import { AuditCategoryBadge } from '@/components/AuditCategoryBadge'
import { formatDateTime } from '@/lib/utils'

const DEFAULT_PAGE_SIZE = 100
/** How often the badge poller checks for new events. 10s is the sweet
 * spot — frequent enough that an investigator notices new activity
 * within the same scroll session, infrequent enough that it doesn't
 * generate meaningful load on the audit collection (each poll is a
 * bounded `count` query gated by `since=topRowTs`). */
const COUNT_POLL_INTERVAL_MS = 10_000
/** Number of columns the empty/loading row spans. Kept as a constant
 * so adding a column updates the colSpan in one place. */
const TABLE_COLSPAN = 10

/** Time-window preset values. `custom` switches on two explicit date
 * inputs; the named presets resolve to a `since` ISO string at
 * fetch time. `all` skips the `since` filter entirely. The default
 * is `7d` — see file header for why. */
type TimeWindow = '24h' | '7d' | '30d' | 'all' | 'custom'

type SortDir = 'asc' | 'desc'

const TIME_WINDOW_OPTIONS: ReadonlyArray<{ value: TimeWindow; label: string }> = [
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'all', label: 'All time' },
  { value: 'custom', label: 'Custom…' },
]

/** Parsed-from-URL filter state. Distinct from `AuditFilter` so the
 * time-window preset (which collapses into a `since` value at fetch
 * time) is first-class instead of having to be inferred from `since`
 * after a round-trip. */
interface FilterState {
  /** Server resolves email → actorUserId via the users collection
   * before querying the audit collection. The audit row never stores
   * email (AU-3 minimization). Only one of email / id is meaningful
   * at a time; the UI no longer surfaces an id input (email is the
   * natural investigator entry point) but the server still accepts
   * `actorUserId` for direct-URL queries / power users. */
  actorEmail: string
  action: string
  targetType: string
  window: TimeWindow
  since: string // custom-only; ISO local date string from <input type="date">
  until: string // custom-only
  sortDir: SortDir
}

const EMPTY_FILTER: FilterState = {
  actorEmail: '',
  action: '',
  targetType: '',
  window: '7d',
  since: '',
  until: '',
  sortDir: 'desc',
}

function parseFilterFromSearchParams(params: URLSearchParams): FilterState {
  const out: FilterState = { ...EMPTY_FILTER }
  out.actorEmail = params.get('actorEmail') ?? ''
  out.action = params.get('action') ?? ''
  out.targetType = params.get('targetType') ?? ''
  const win = params.get('window')
  if (win === '24h' || win === '7d' || win === '30d' || win === 'all' || win === 'custom') {
    out.window = win
  }
  out.since = params.get('since') ?? ''
  out.until = params.get('until') ?? ''
  const sd = params.get('sortDir')
  if (sd === 'asc' || sd === 'desc') out.sortDir = sd
  return out
}

function applyFilterToSearchParams(
  current: URLSearchParams,
  next: FilterState,
): URLSearchParams {
  const out = new URLSearchParams(current)
  // Drop the retired `actorUserId` param so a previously-shared or
  // hand-edited URL doesn't leave a stale visible param the UI no
  // longer applies. PR #534 Copilot review. The backend still
  // accepts the param for direct-URL queries, so this only affects
  // the bookmarkable URL — the actual filter remains email-only.
  out.delete('actorUserId')
  const setOrDelete = (key: string, value: string) => {
    if (value) out.set(key, value)
    else out.delete(key)
  }
  setOrDelete('actorEmail', next.actorEmail)
  setOrDelete('action', next.action)
  setOrDelete('targetType', next.targetType)
  // Skip the URL-default to keep the common case clean.
  if (next.window !== '7d') out.set('window', next.window)
  else out.delete('window')
  setOrDelete('since', next.window === 'custom' ? next.since : '')
  setOrDelete('until', next.window === 'custom' ? next.until : '')
  if (next.sortDir !== 'desc') out.set('sortDir', next.sortDir)
  else out.delete('sortDir')
  return out
}

/** Convert a UI-supplied date string into an ISO 8601 string, or
 * return `undefined` if the input doesn't parse (so the filter is
 * omitted rather than crashing the page). Critical for URL-backed
 * filters: a deep-link with `?window=custom&since=not-a-date` would
 * otherwise throw a `RangeError` inside `resolveWindow` and crash
 * the render before the API call. PR #530 Copilot review. */
function toIsoOrUndefined(s: string): string | undefined {
  if (!s) return undefined
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return undefined
  return d.toISOString()
}

/** Render the audit row's timestamp without crashing on a malformed
 * `event.ts`. The server validates on write, but a future schema
 * drift (or a corrupted row) could surface an unparseable string —
 * render it raw instead of throwing inside the cell. PR #530
 * Copilot review (same defensive pattern as `toIsoOrUndefined`).
 *
 * #1218 Group E: audit log is a forensic surface — switched from the
 * user's preferred tz (Phase 1 #724) to `'absoluteUTC'` so investigators
 * reading audit rows across timezones (support tickets, cross-team
 * coordination) see one unambiguous stamp. */
function formatTsSafe(ts: string | Date | undefined): string {
  return formatDateTime(ts, 'absoluteUTC')
}

/** Resolve the preset window into a `since` ISO string. `all` returns
 * `undefined` so the filter is omitted entirely. */
function resolveWindow(window: TimeWindow, customSince: string, customUntil: string): {
  since?: string
  until?: string
} {
  if (window === 'all') return {}
  if (window === 'custom') {
    return {
      since: toIsoOrUndefined(customSince),
      until: toIsoOrUndefined(customUntil),
    }
  }
  const now = Date.now()
  const ms = window === '24h' ? 24 * 60 * 60 * 1000
    : window === '7d' ? 7 * 24 * 60 * 60 * 1000
    : /* 30d */ 30 * 24 * 60 * 60 * 1000
  return { since: new Date(now - ms).toISOString() }
}

/** Build the API filter shape from the UI filter + a pagination
 * offset. Centralized so the initial fetch and the "Load more"
 * fetch use the same logic. */
function buildApiFilter(state: FilterState, offset: number): AuditFilter {
  const { since, until } = resolveWindow(state.window, state.since, state.until)
  const filter: AuditFilter = {
    limit: DEFAULT_PAGE_SIZE,
    offset,
    sortDir: state.sortDir,
  }
  const actorEmail = state.actorEmail.trim()
  if (actorEmail) filter.actorEmail = actorEmail
  if (state.action.trim()) filter.action = state.action.trim()
  if (state.targetType.trim()) filter.targetType = state.targetType.trim()
  if (since) filter.since = since
  if (until) filter.until = until
  return filter
}

export function AuditPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const filter = useMemo(() => parseFilterFromSearchParams(searchParams), [searchParams])

  const setFilter = useCallback(
    (next: FilterState) => {
      setSearchParams((prev) => applyFilterToSearchParams(prev, next), { replace: true })
    },
    [setSearchParams],
  )

  // Result state. `rows` accumulates across "Load more" clicks until
  // the filter changes. `hasMore` is inferred from the last response
  // length matching the requested limit — the server omits `total`
  // deliberately (countDocuments dominates the request budget).
  const [rows, setRows] = useState<AuditEventRow[]>([])
  // Display-only enrichment from the server's `actors` map. Keyed
  // by actorUserId; missing keys = unresolvable user (deleted /
  // legacy) → table cell falls back to bare id. Merged across
  // "Load more" pages just like rows.
  const [actors, setActors] = useState<Record<string, { email?: string; name?: string }>>({})
  // Display-only context-chain map. Keyed by event _id; missing =
  // unresolvable (server fail-soft on DB blip, or target type that
  // doesn't fit any chain). Merged across "Load more" pages.
  const [contexts, setContexts] = useState<Record<string, AuditContext>>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  // "N new events" badge — number of events newer than the current
  // top row that the badge poller has observed. Reset to 0 every
  // time the user refetches (filter change OR clicking the badge).
  const [newCount, setNewCount] = useState(0)
  // Bumped to force a refetch when the user clicks the badge — same
  // effect dep change that a filter edit causes, without needing a
  // synthetic URL mutation.
  const [refetchToken, setRefetchToken] = useState(0)

  // Re-fetch from offset 0 whenever the filter changes (URL string)
  // OR the user clicks the badge (refetchToken bump). Including both
  // in the dep array preserves the "fresh data when filter changes"
  // story while letting the badge piggyback on the same path.
  const searchKey = searchParams.toString()
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    adminAuditAPI
      .list(buildApiFilter(filter, 0))
      .then((res) => {
        if (cancelled) return
        setRows(res.events)
        setActors(res.actors)
        setContexts(res.contexts ?? {})
        setHasMore(res.events.length === DEFAULT_PAGE_SIZE)
        setNewCount(0)
      })
      .catch((e) => {
        if (cancelled) return
        setError(e instanceof Error ? e.message : 'Failed to load audit events')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // `searchKey` covers everything `filter` is derived from.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKey, refetchToken])

  // ── New-events poller ──────────────────────────────────────────
  // Polls `/audit/count?since=topRowTs` every COUNT_POLL_INTERVAL_MS
  // while the user is on the page (desc sort only — see below).
  // The badge surfaces the count; clicking it triggers a refetch
  // via `refetchToken`.
  //
  // **Disabled on asc sort.** Ascending order shows oldest-first
  // historical exploration; the "newest event the user has seen"
  // anchor doesn't exist (the newest events sit in later pages the
  // user hasn't loaded). Polling `max(ts)` of a partial page would
  // count every newer-but-already-existing row as "new" and surface
  // a misleadingly large badge immediately after loading. We just
  // skip the poll when the view is asc — investigators on historical
  // data don't benefit from a live indicator anyway. PR #534
  // Copilot review.
  //
  // We keep the latest filter in a ref so the interval callback
  // sees fresh values without re-creating the interval on every
  // filter edit (cheaper and avoids missing a tick during rapid edits).
  const filterRef = useRef(filter)
  filterRef.current = filter
  const topTs = useMemo(() => {
    if (rows.length === 0) return null
    let max = 0
    for (const r of rows) {
      const t = new Date(r.ts).getTime()
      if (!Number.isNaN(t) && t > max) max = t
    }
    return max > 0 ? new Date(max).toISOString() : null
  }, [rows])
  // Number of loaded rows whose ts equals the anchor — used to
  // tie-break the $gte count below. Pre-computed once per page
  // change so the poller callback doesn't iterate rows on every
  // tick. PR #534 Copilot review.
  const topTsTiedRowCount = useMemo(() => {
    if (topTs === null) return 0
    const maxMs = new Date(topTs).getTime()
    let n = 0
    for (const r of rows) {
      if (new Date(r.ts).getTime() === maxMs) n++
    }
    return n
  }, [rows, topTs])
  const topTsRef = useRef(topTs)
  topTsRef.current = topTs
  const topTsTiedRowCountRef = useRef(topTsTiedRowCount)
  topTsTiedRowCountRef.current = topTsTiedRowCount

  const pollEnabled = filter.sortDir === 'desc'

  useEffect(() => {
    if (topTs === null || !pollEnabled) {
      setNewCount(0)
      return
    }
    let cancelled = false
    async function poll() {
      const since = topTsRef.current
      if (!since) return
      try {
        // Build a filter that mirrors the current view but overrides
        // `since` with the top row's ts — "events newer than what
        // the user is currently looking at, under the same filter."
        // We DO carry the user's `until` (closed historical windows
        // mean the badge respects the bound — PR #534 Copilot review).
        // We DON'T carry the user's `since`/window into the poll;
        // overriding with topTs is strictly tighter than any window
        // that already included topTs.
        const f = buildApiFilter(filterRef.current, 0)
        delete f.limit
        delete f.offset
        delete f.sortDir
        f.since = since
        const { count } = await adminAuditAPI.countNew(f)
        if (cancelled) return
        // Subtract the number of loaded rows at the anchor timestamp.
        // The server's $gte is inclusive, so the count includes every
        // row whose ts == anchor — not just the single row the
        // earlier `subtract-1` heuristic assumed. With ties (batched
        // writes or millisecond-coarse timestamps), `count - 1` would
        // surface false-positive "new event" badges. PR #534 Copilot
        // review. Floor at 0 in case a row was removed (rare — would
        // require a manual collection edit).
        setNewCount(Math.max(0, count - topTsTiedRowCountRef.current))
      } catch {
        // Polling failures are non-actionable for the user — don't
        // surface them. The investigation table still works; the
        // badge just won't update until the next successful tick.
      }
    }
    // Kick off an immediate poll so the badge appears without a
    // 10s wait for the first interval to fire — then settle into
    // the cadence.
    poll()
    const id = setInterval(poll, COUNT_POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      clearInterval(id)
    }
    // Restart the interval when topTs changes (so each refresh
    // anchors against the newest loaded row) or when polling
    // enabled-ness flips (sort dir change). NOT on `filter` —
    // the ref above gives the callback the latest filter value
    // without thrashing the interval.
  }, [topTs, pollEnabled])

  async function loadMore() {
    if (loading || !hasMore) return
    setLoading(true)
    setError(null)
    try {
      const res = await adminAuditAPI.list(buildApiFilter(filter, rows.length))
      setRows((prev) => [...prev, ...res.events])
      // Merge enrichment maps so prior-page entries stay resolvable
      // after a "Load more". Newer-page entry wins on collision
      // (negligible — the user data wouldn't have changed between
      // page loads).
      setActors((prev) => ({ ...prev, ...res.actors }))
      setContexts((prev) => ({ ...prev, ...(res.contexts ?? {}) }))
      setHasMore(res.events.length === DEFAULT_PAGE_SIZE)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load more audit events')
    } finally {
      setLoading(false)
    }
  }

  function toggleSort() {
    setFilter({ ...filter, sortDir: filter.sortDir === 'desc' ? 'asc' : 'desc' })
  }

  function refetchFromBadge() {
    setNewCount(0)
    setRefetchToken((n) => n + 1)
  }

  return (
    <div className="space-y-6 max-w-full">
      <div>
        <h1 className="text-2xl font-semibold">Audit Log</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Cross-org audit trail of state-changing admin actions (membership, credentials, org lifecycle,
          superadmin escalations, runtime-config edits). Read-only.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filter</CardTitle>
        </CardHeader>
        <CardContent>
          <FilterBar filter={filter} onChange={setFilter} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Events {rows.length > 0 && <span className="text-sm font-normal text-muted-foreground">({rows.length} loaded{hasMore ? '+' : ''})</span>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {newCount > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={refetchFromBadge}
              className="w-full"
              aria-label={`${newCount} new event${newCount === 1 ? '' : 's'} — click to refresh`}
            >
              {newCount} new event{newCount === 1 ? '' : 's'} — click to refresh
            </Button>
          )}
          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
          <div className="border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  {/* `aria-sort` is only valid on the column header (the
                      `<th>`), not on a nested button. PR #534 Copilot
                      review — moved here so assistive tech announces
                      the current sort state. The button remains the
                      interactive control. */}
                  <TableHead aria-sort={filter.sortDir === 'desc' ? 'descending' : 'ascending'}>
                    <button
                      type="button"
                      onClick={toggleSort}
                      className="inline-flex items-center gap-1 hover:text-foreground"
                      aria-label={`Sort by timestamp ${filter.sortDir === 'desc' ? 'ascending' : 'descending'}`}
                    >
                      Timestamp
                      <span aria-hidden="true">{filter.sortDir === 'desc' ? '↓' : '↑'}</span>
                    </button>
                  </TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Org</TableHead>
                  <TableHead>Workspace</TableHead>
                  <TableHead>App</TableHead>
                  <TableHead>Variant</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Result</TableHead>
                  <TableHead>Meta</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((ev) => (
                  <AuditEventRowView
                    key={ev._id}
                    event={ev}
                    actor={actors[ev.actorUserId]}
                    context={contexts[ev._id]}
                  />
                ))}
                {rows.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={TABLE_COLSPAN} className="text-center text-sm text-muted-foreground py-8">
                      No events match the filter.
                    </TableCell>
                  </TableRow>
                )}
                {loading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={TABLE_COLSPAN} className="text-center text-sm text-muted-foreground py-8">
                      Loading…
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <div className="flex justify-center">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={loadMore}
              disabled={loading || !hasMore}
            >
              {loading && rows.length > 0 ? 'Loading…' : hasMore ? 'Load more' : 'No more events'}
            </Button>
          </div>
        </CardContent>
      </Card>

    </div>
  )
}

// ── Filter bar ─────────────────────────────────────────────────────────

function FilterBar({
  filter,
  onChange,
}: {
  filter: FilterState
  onChange: (next: FilterState) => void
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <div className="space-y-1">
        <Label htmlFor="filter-actor-email">Actor email</Label>
        <Input
          id="filter-actor-email"
          type="search"
          value={filter.actorEmail}
          onChange={(e) => onChange({ ...filter, actorEmail: e.target.value })}
          placeholder="e.g. alice@example.com"
          clearable
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="filter-action">Action</Label>
        <Combobox
          id="filter-action"
          value={filter.action}
          onChange={(v) => onChange({ ...filter, action: v })}
          options={AUDIT_ACTION_KEYS}
          // Bumped from the default 50 — adding the Slack action set
          // (#1048 Slice 1) brought the catalog past 50 entries, so a
          // truncating cap would hide a chunk of the suggestion list
          // when the user first focuses the input.
          maxOptions={100}
          placeholder="e.g. member.invited"
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="filter-target-type">Target type</Label>
        <Combobox
          id="filter-target-type"
          value={filter.targetType}
          onChange={(v) => onChange({ ...filter, targetType: v })}
          options={AUDIT_TARGET_TYPE_KEYS}
          placeholder="e.g. org"
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="filter-window">Time window</Label>
        <Select
          id="filter-window"
          options={TIME_WINDOW_OPTIONS as unknown as Array<{ value: string; label: string }>}
          value={filter.window}
          onChange={(e) => onChange({ ...filter, window: e.target.value as TimeWindow })}
        />
      </div>
      {filter.window === 'custom' && (
        <>
          <div className="space-y-1">
            <Label htmlFor="filter-since">Since</Label>
            <Input
              id="filter-since"
              type="date"
              value={filter.since}
              onChange={(e) => onChange({ ...filter, since: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="filter-until">Until</Label>
            <Input
              id="filter-until"
              type="date"
              value={filter.until}
              onChange={(e) => onChange({ ...filter, until: e.target.value })}
            />
          </div>
        </>
      )}
    </div>
  )
}

// ── Per-row component ──────────────────────────────────────────────────

/** Truncate an actor id to its leading 8 chars (with ellipsis) for
 * display alongside the email. 8 chars = ~1T combinations, plenty
 * for visual disambiguation; full id is in the meta panel + the
 * copy-paste affordance (the title attr) for cross-reference. */
function truncateActorId(id: string): string {
  if (id.length <= 12) return id
  return id.slice(0, 8) + '…'
}

/** Render a single chain-context cell. Server returns `{id, name}`
 * when resolvable; we render the name (with the id in the title for
 * copy-paste). When the layer is absent for this event, render `—`
 * — UNLESS a bare-id fallback is provided (used for the Org column,
 * which can fall back to `actorOrgId` carried on the row itself).
 * PR #534 Copilot review — the previous design surfaced `—` whenever
 * the chain resolver missed, which broke the "investigator-visible
 * org context" guarantee for events with an `actorOrgId` but no
 * resolvable chain (resolver miss, DB blip, system-level target). */
function ContextCell({ entry, idFallback }: { entry?: { id: string; name: string }; idFallback?: string }) {
  if (entry) {
    return (
      <span className="text-xs" title={entry.id}>
        {entry.name}
      </span>
    )
  }
  if (idFallback) {
    return (
      <span className="text-xs font-mono text-muted-foreground" title={idFallback}>
        {idFallback}
      </span>
    )
  }
  return <span className="text-xs text-muted-foreground">—</span>
}

function AuditEventRowView({
  event,
  actor,
  context,
}: {
  event: AuditEventRow
  actor?: { email?: string; name?: string }
  context?: AuditContext
}) {
  const [expanded, setExpanded] = useState(false)
  const hasMeta = event.meta && Object.keys(event.meta).length > 0
  const hasBefore = event.before && Object.keys(event.before).length > 0
  const hasAfter = event.after && Object.keys(event.after).length > 0
  const expandable = hasMeta || hasBefore || hasAfter || !!event.failureReason

  // Display rule (AU-3 minimization aware): the audit ROW only
  // stores actorUserId. The DISPLAY enriches with email/name when
  // resolvable via the server's `actors` map. Falls back to the
  // bare id when the user can't be resolved (deleted account /
  // legacy row). Title attribute always carries the FULL id so an
  // investigator can copy-paste regardless of which branch rendered.
  const hasEmail = actor?.email
  const actorDisplay = hasEmail
    ? `${actor!.email} · ${truncateActorId(event.actorUserId)}`
    : event.actorUserId

  return (
    <>
      <TableRow>
        <TableCell className="text-xs whitespace-nowrap align-top">
          {formatTsSafe(event.ts)}
        </TableCell>
        <TableCell className="text-xs align-top">
          <div
            className={hasEmail ? '' : 'font-mono'}
            title={event.actorUserId}
          >
            {actorDisplay}
          </div>
        </TableCell>
        <TableCell className="text-xs align-top">
          {/* Category badge (#1119) — derived from the action's resource
              prefix; the raw key stays below for exact investigation. */}
          <AuditCategoryBadge action={event.action} />
          <div className="mt-1 font-mono">{event.action}</div>
        </TableCell>
        {/* Org cell falls back to the bare `actorOrgId` carried on
            the row when the chain resolver couldn't surface a name
            (resolver miss, DB blip, system-level target type with
            no chain). Other layers have no bare-id fallback because
            the row doesn't carry them. PR #534 Copilot review. */}
        <TableCell className="align-top"><ContextCell entry={context?.org} idFallback={event.actorOrgId} /></TableCell>
        <TableCell className="align-top"><ContextCell entry={context?.workspace} /></TableCell>
        <TableCell className="align-top"><ContextCell entry={context?.app} /></TableCell>
        <TableCell className="align-top"><ContextCell entry={context?.variant} /></TableCell>
        <TableCell className="text-xs align-top">
          <div className="font-mono">{event.targetType}</div>
          <div className="text-muted-foreground text-[10px] font-mono break-all">{event.targetId}</div>
        </TableCell>
        <TableCell className="align-top">
          <Badge variant={event.result === 'success' ? 'success' : 'destructive'}>
            {event.result}
          </Badge>
        </TableCell>
        <TableCell className="align-top">
          {expandable ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              aria-controls={`audit-meta-${event._id}`}
            >
              {expanded ? 'Hide' : 'Show'}
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </TableCell>
      </TableRow>
      {expanded && expandable && (
        <TableRow>
          <TableCell colSpan={TABLE_COLSPAN} className="bg-muted/40">
            <div id={`audit-meta-${event._id}`} className="space-y-2">
              {event.failureReason && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                    Failure reason
                  </div>
                  <div className="text-xs font-mono text-destructive break-all">{event.failureReason}</div>
                </div>
              )}
              {hasBefore && (
                <JsonBlock label="Before" value={event.before} />
              )}
              {hasAfter && (
                <JsonBlock label="After" value={event.after} />
              )}
              {hasMeta && (
                <JsonBlock label="Meta" value={event.meta} />
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

/** Render a JSON object as formatted text inside a `<pre>` block.
 * React's text-content escaping ensures any embedded `<` / `&` /
 * quotes are inert — operator-supplied values inside `meta` can NOT
 * inject markup. */
function JsonBlock({ label, value }: { label: string; value: unknown }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{label}</div>
      <pre className="text-xs font-mono bg-background border rounded p-2 overflow-auto max-h-64">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  )
}

export default AuditPage
